import { observer } from "mobx-react-lite";
import bodyStylesManager from "../../../stores/body-styles-manager";
import DimensionsBox from "../shared/satori-ui/dimensions-box";
import { TypeOutline } from "lucide-react";
import { ITypographyRole, ITypoStyles } from "../../../stores/state-interfaces";
import type { TextTransformType } from "../../../stores/state-interfaces";
import { 
  ACTIVE_TYPO_ROLES, 
  FONT_WEIGHTS, NOPERCENT_UNITS, FONT_STYLES, TEXT_DECORATIONS, TEXT_TRANSFORMATIONS } from "../../../types/constants";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";

function safeDimension(dim?: { value?: string; unit?: string }, unitFallback = "px") {
  return {
    value: dim?.value ?? "",
    unit: dim?.unit ?? unitFallback,
  };
}

export default observer(function TypographySection() {
  // Get role and heading context
  const activeRole = bodyStylesManager.getActiveTextRole();
  const headingLevel = bodyStylesManager.getActiveHeadingLevel();

  // Get current role key
  const roleKey = (
    activeRole.toLowerCase() === "heading" ? headingLevel : activeRole.toLowerCase()
  ) as keyof ITypoStyles;

  // Get full typography state
  const typoStyles: ITypoStyles | undefined = bodyStylesManager.getTypographyStyles();
  // If typoStyles is undefined, or the requested role is undefined, fallback to empty object
  const current: ITypographyRole = (typoStyles && typoStyles[roleKey]) || {};

  const lineHeightField = safeDimension(current.lineHeight, "");
  const letterSpacingField = safeDimension(current.letterSpacing, "px");

  // Utility setter
  function updateCurrentRoleField<K extends keyof ITypographyRole>(
    field: K,
    value: ITypographyRole[K]
  ) {
    const newRole: ITypographyRole = { ...current, [field]: value };
    bodyStylesManager.setTypographyRole(roleKey, newRole);
  }

  return (
    <SatoriCollapsiblePanel
      icon={<TypeOutline size={18} />}
      title={"Typography"}
      defaultOpen={true}
    >
      <div className="flex flex-col gap-3">
        {/* Role Selector */}
        <SatoriDropDownList
          label={"Select Role:"}
          value={activeRole}
          options={ACTIVE_TYPO_ROLES}
          isFontFamily={false}
          onChange={(val) => bodyStylesManager.setActiveTextRole(val)}
        />

        {/* Heading Level Selector (only if role is Heading) */}
        {activeRole === "heading" && (
          <div className="flex w-full items-center justify-evenly border border-gray-300 rounded text-xs">
            {["h1", "h2", "h3", "h4", "h5", "h6"].map((h) => (
              <button
                key={h}
                onClick={() => bodyStylesManager.setActiveHeadingLevel(h)}
                className={`w-1/6 aspect-square p-2 ${
                  headingLevel === h
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {h.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        {/* Font Family */}
        <SatoriDropDownList
          label={"Font Family"}
          value={current.fontFamily || "Inter, sans-serif"}
          isFontFamily={true}
          onChange={(val) => updateCurrentRoleField("fontFamily", val)}
        />

        {/* Font Size */}
        <DimensionsBox
          label="Font Size"
          value={current.fontSize?.value || ""}
          unit={current.fontSize?.unit || "rem"}
          units={NOPERCENT_UNITS}
          onValueChange={(val) =>
            updateCurrentRoleField("fontSize", {
              value: val,
              unit: current.fontSize?.unit || "rem",
            })
          }
          onUnitChange={(unit) =>
            updateCurrentRoleField("fontSize", {
              value: current.fontSize?.value || "",
              unit, // always defined from the select
            })
          }
        />

        {/* Font Weight */}
        {"fontWeight" in current && (
          <SatoriDropDownList
            layout="horizontal"
            label={"Font Weight"}
            options={FONT_WEIGHTS}
            isFontFamily={false}
            value={current.fontWeight || ""}
            onChange={(val) => updateCurrentRoleField("fontWeight", val)}
          />
        )}

        <SatoriColorPicker
          label={"Text Color"}
          value={current.color || "#222222"}
          onChange={(val) => updateCurrentRoleField("color", val)}
        />

        {/* Hover Color (only for Links) */}
        {roleKey === "links" && (
          <SatoriColorPicker
            label={"Hover Text"}
            value={current.hoverColor || "#2e6ef7"}
            onChange={(val) => updateCurrentRoleField("hoverColor", val)}
          />
        )}

        {/* Font Style (for quote, etc.) */}
        {"fontStyle" in current && (
          <SatoriDropDownList
            label={"Font Style"}
            options={FONT_STYLES}
            isFontFamily={false}
            value={current.fontStyle || "normal"}
            onChange={(val) => updateCurrentRoleField("fontStyle", val)}
          />
        )}

        <DimensionsBox
          label="Line Height"
          value={lineHeightField.value || ""}
          unit={lineHeightField.unit || "px"}
          units={NOPERCENT_UNITS}
          onValueChange={(val) =>
            updateCurrentRoleField("lineHeight", {
              value: val,
              unit: lineHeightField.unit,
            })
          }
          onUnitChange={(unit) =>
            updateCurrentRoleField("lineHeight", {
              value: lineHeightField.value,
              unit,
            })
          }
        />

        <DimensionsBox
          label="Letter Spacing"
          value={letterSpacingField.value}
          unit={letterSpacingField.unit}
          units={NOPERCENT_UNITS}
          onValueChange={(val) =>
            updateCurrentRoleField("letterSpacing", {
              value: val,
              unit: letterSpacingField.unit,
            })
          }
          onUnitChange={(unit) =>
            updateCurrentRoleField("letterSpacing", {
              value: letterSpacingField.value,
              unit,
            })
          }
        />

        {/* Text Decoration */}
        <SatoriDropDownList
          layout="horizontal"
          label={"Text Decoration"}
          options={TEXT_DECORATIONS}
          isFontFamily={false}
          value={current.textDecoration || "none"}
          onChange={(val) => updateCurrentRoleField("textDecoration", val)}
        />

        {/* Text Transform */}
        <SatoriDropDownList
          layout="horizontal"
          label={"Text Transform"}
          options={TEXT_TRANSFORMATIONS}
          isFontFamily={false}
          value={current.textTransform || "none"}
          onChange={(val) => updateCurrentRoleField("textTransform", val as TextTransformType)}
        />
      </div>

      {/* (Optional: add fields for lineHeight, letterSpacing, etc. here) */}
    </SatoriCollapsiblePanel>
  );
});
